<script setup>
// Donusumler sekmesi: pikseller ve ozel donusumler yan yana.
//
// Ikisi ayni ekranda cunku bir ozel donusum her zaman bir piksele bagli
// tanimlanir; kullanicinin piksel kimligini baska sayfadan kopyalamasi
// gerekmesin diye piksel listesi hemen solda duruyor.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAlert } from 'dashboard/composables';
import { aracCalistir, diziyeCevir, sayiBicimle } from './kopru.js';
import AracTablosu from './AracTablosu.vue';

const props = defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: null },
});

const { t } = useI18n();
const route = useRoute();

const pikseller = ref([]);
const pikselYukleniyor = ref(false);
const pikselHata = ref('');

const donusumler = ref([]);
const donusumYukleniyor = ref(false);
const donusumHata = ref('');

const seciliPiksel = ref(null);
const pikselOzeti = ref('');
// Ayrinti artik metin degil nesne tutuyor: ekranda ham JSON yerine
// alan/deger satirlari basiliyor.
const pikselVerisi = ref(null);
const pikselOlaylari = ref([]);
const veriKalitesi = ref('');
const ayrintiYukleniyor = ref(false);
const ayrintiHata = ref('');

const formAcik = ref(false);
const yeniAd = ref('');
const yeniPiksel = ref('');
const yeniOlay = ref('PURCHASE');
const yeniUrl = ref('');
const kaydediliyor = ref(false);

const accountId = computed(() => route.params.accountId);

// Meta'nin standart ozel donusum olay turleri; serbest metin kabul etmiyor.
const olayTurleri = [
  'PURCHASE',
  'LEAD',
  'COMPLETE_REGISTRATION',
  'ADD_TO_CART',
  'INITIATED_CHECKOUT',
  'CONTENT_VIEW',
  'SEARCH',
  'OTHER',
];

// Piksel ayrintisinda donen alanlarin okunur etiketleri. Listede olmayan
// anahtar oldugu gibi gosterilir; olmayan bir karsiligi uydurmak kullaniciyi
// yanlis bilgilendirir.
const PIKSEL_ALAN_ETIKETLERI = {
  id: 'KIMLIK',
  name: 'AD',
  code: 'KOD',
  creation_time: 'OLUSTURULMA',
  last_fired_time: 'SON_OLAY',
  is_created_by_business: 'ISLETME_OLUSTURDU',
  is_unavailable: 'KULLANILAMIYOR',
  data_use_setting: 'VERI_KULLANIMI',
  enable_automatic_matching: 'OTOMATIK_ESLESTIRME',
  automatic_matching_fields: 'ESLESTIRME_ALANLARI',
  first_party_cookie_status: 'BIRINCI_TARAF_CEREZ',
  owner_business: 'SAHIP_ISLETME',
  owner_ad_account: 'SAHIP_HESAP',
  account_id: 'HESAP_KIMLIGI',
  pixel_id: 'PIKSEL_KIMLIGI',
  custom_event_type: 'OLAY_TURU',
  description: 'ACIKLAMA',
  status: 'DURUM',
  rule: 'KURAL',
};

const TARIH_ALANLARI = new Set([
  'creation_time',
  'created_time',
  'last_fired_time',
  'updated_time',
  'start_time',
  'end_time',
]);

// Salt tarih metnini (2024-05-01) dogrudan Date'e vermek UTC olarak cozulup
// yerel saate cevrildigi icin gunu kaydirabiliyor; saat ekleyerek yerel
// okuyoruz ve salt tarihte saat yazdirmiyoruz.
const tarihBicimle = deger => {
  const metin = String(deger ?? '');
  if (!metin) return '';
  const saltTarih = /^\d{4}-\d{2}-\d{2}$/.test(metin);
  const tarih = saltTarih ? new Date(`${metin}T00:00:00`) : new Date(metin);
  if (Number.isNaN(tarih.getTime())) return metin;
  return saltTarih
    ? tarih.toLocaleDateString('tr-TR', { dateStyle: 'medium' })
    : tarih.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
};

const pikselEtiket = anahtar => {
  const ek = PIKSEL_ALAN_ETIKETLERI[anahtar];
  return ek ? t(`META_ADS.ISTATISTIKLER.DONUSUMLER.PIKSEL_ALAN.${ek}`) : anahtar;
};

// Bos deger '' donuyor ve satir hic basilmiyor: olmayan alani "—" ile
// gostermek paneli gereksiz uzatir.
const pikselDeger = (anahtar, ham) => {
  if (ham === null || ham === undefined || ham === '') return '';
  if (typeof ham === 'boolean') {
    return ham
      ? t('META_ADS.ISTATISTIKLER.ORTAK.EVET')
      : t('META_ADS.ISTATISTIKLER.ORTAK.HAYIR');
  }
  if (Array.isArray(ham)) {
    if (!ham.length) return '';
    // Duz dizi okunur sekilde yan yana yazilir; nesne dizisi satira sigmaz,
    // sayisini vermek daha anlamli.
    const duz = ham.every(o => o === null || typeof o !== 'object');
    return duz
      ? ham.join(', ')
      : t('META_ADS.ISTATISTIKLER.ORTAK.OGE_SAYISI', { sayi: ham.length });
  }
  if (typeof ham === 'object') {
    // Ic ice nesnede kullaniciya anlam tasiyan ad ya da kimlik; ikisi de
    // yoksa alan sayisi. Nesneyi acip basmak paneli okunmaz yapar.
    const ad = typeof ham.name === 'string' ? ham.name : '';
    const kimlik = ['string', 'number'].includes(typeof ham.id)
      ? String(ham.id)
      : '';
    return (
      ad ||
      kimlik ||
      t('META_ADS.ISTATISTIKLER.ORTAK.ALAN_SAYISI', {
        sayi: Object.keys(ham).length,
      })
    );
  }
  if (TARIH_ALANLARI.has(anahtar)) return tarihBicimle(ham);
  return String(ham);
};

// Ham JSON blogunun yerini alan/deger satirlari aldi; kullanici suslu
// parantezleri degil, alanin ne oldugunu okumali.
const pikselAlanlari = computed(() => {
  const veri = pikselVerisi.value;
  if (!veri || typeof veri !== 'object') return [];
  return Object.entries(veri)
    .map(([anahtar, ham]) => ({
      anahtar,
      etiket: pikselEtiket(anahtar),
      deger: pikselDeger(anahtar, ham),
    }))
    .filter(alan => alan.deger !== '');
});

const pikselSutunlari = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.ISTATISTIKLER.DONUSUMLER.SUTUN.AD') },
  { anahtar: 'id', baslik: t('META_ADS.ISTATISTIKLER.DONUSUMLER.SUTUN.KIMLIK') },
  {
    anahtar: 'last_fired_time',
    baslik: t('META_ADS.ISTATISTIKLER.DONUSUMLER.SUTUN.SON_OLAY'),
    // Meta bu alani ISO damgasi olarak dondurur; tabloda okunur tarih duruyor.
    bicim: deger => (deger ? tarihBicimle(deger) : '—'),
  },
]);

const donusumSutunlari = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.ISTATISTIKLER.DONUSUMLER.SUTUN.AD') },
  {
    anahtar: 'custom_event_type',
    baslik: t('META_ADS.ISTATISTIKLER.DONUSUMLER.SUTUN.OLAY_TURU'),
  },
  {
    anahtar: 'count',
    baslik: t('META_ADS.ISTATISTIKLER.DONUSUMLER.SUTUN.SAYI'),
    bicim: deger => (deger === null || deger === undefined ? '—' : sayiBicimle(deger)),
  },
]);

const olaySutunlari = computed(() => [
  {
    anahtar: 'event',
    baslik: t('META_ADS.ISTATISTIKLER.DONUSUMLER.SUTUN.OLAY'),
  },
  {
    anahtar: 'count',
    baslik: t('META_ADS.ISTATISTIKLER.DONUSUMLER.SUTUN.SAYI'),
    bicim: deger => (deger === null || deger === undefined ? '—' : sayiBicimle(deger)),
  },
]);

const pikselleriYukle = async () => {
  if (!props.hesap) return;
  pikselYukleniyor.value = true;
  pikselHata.value = '';
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_pixels', {
      account_id: props.hesap,
    });
    pikseller.value = diziyeCevir(sonuc);
  } catch (e) {
    pikselHata.value = e.response?.data?.error || e.message;
    pikseller.value = [];
  } finally {
    pikselYukleniyor.value = false;
  }
};

const donusumleriYukle = async () => {
  if (!props.hesap) return;
  donusumYukleniyor.value = true;
  donusumHata.value = '';
  try {
    const sonuc = await aracCalistir(
      accountId.value,
      'ads_get_custom_conversions',
      { account_id: props.hesap }
    );
    donusumler.value = diziyeCevir(sonuc);
  } catch (e) {
    donusumHata.value = e.response?.data?.error || e.message;
    donusumler.value = [];
  } finally {
    donusumYukleniyor.value = false;
  }
};

// Uc sorgu tek dugmede: ayrinti, olaylar ve veri kalitesi ayri ayri
// tiklatmak yerine piksel secilince birlikte geliyor. Biri desteklenmiyorsa
// digerleri yine dolsun diye her biri kendi try blogunda.
const pikselSec = async piksel => {
  // Kimliksiz satirda uc sorgu birden bos pixel_id ile gider ve uc ayri hata
  // dondururdu; hicbiri kullaniciya sorunun ne oldugunu anlatmaz.
  if (!piksel?.id) return;
  seciliPiksel.value = piksel;
  yeniPiksel.value = piksel.id;
  ayrintiYukleniyor.value = true;
  ayrintiHata.value = '';
  pikselOzeti.value = '';
  pikselVerisi.value = null;
  pikselOlaylari.value = [];
  veriKalitesi.value = '';

  const kimlik = piksel?.id;
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_pixel_details', {
      pixel_id: kimlik,
    });
    pikselOzeti.value = sonuc?.ozet || '';
    // Arac tek kayit icin bazen dizi dondurur; ilk kaydi aliyoruz ki panel
    // bos gorunmesin.
    const veri = Array.isArray(sonuc?.veri) ? sonuc.veri[0] : sonuc?.veri;
    pikselVerisi.value = veri && typeof veri === 'object' ? veri : null;
  } catch (e) {
    ayrintiHata.value = e.response?.data?.error || e.message;
  }

  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_pixel_events', {
      pixel_id: kimlik,
    });
    pikselOlaylari.value = diziyeCevir(sonuc);
  } catch (e) {
    ayrintiHata.value = ayrintiHata.value || e.response?.data?.error || e.message;
  }

  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_dataset_quality', {
      pixel_id: kimlik,
    });
    veriKalitesi.value = sonuc?.ozet || '';
  } catch (e) {
    ayrintiHata.value = ayrintiHata.value || e.response?.data?.error || e.message;
  }

  ayrintiYukleniyor.value = false;
};

const donusumOlustur = async () => {
  if (!yeniAd.value || !yeniPiksel.value) return;
  // Ozel donusum silinemiyor ve raporlamayi dogrudan etkiliyor; yanlislikla
  // olusturulmasin diye once onay aliniyor.
  const onay = window.confirm(
    t('META_ADS.ISTATISTIKLER.DONUSUMLER.OLUSTUR_ONAY', { ad: yeniAd.value })
  );
  if (!onay) return;

  kaydediliyor.value = true;
  donusumHata.value = '';
  try {
    const argumanlar = {
      account_id: props.hesap,
      name: yeniAd.value,
      pixel_id: yeniPiksel.value,
      custom_event_type: yeniOlay.value,
    };
    // Kural yalnizca URL verildiyse gonderiliyor; bos kural Meta tarafinda
    // tum trafigi eslestirir ve donusumu anlamsiz yapar.
    if (yeniUrl.value) {
      argumanlar.rule = { url: { i_contains: yeniUrl.value } };
    }
    await aracCalistir(
      accountId.value,
      'ads_create_custom_conversion',
      argumanlar,
      { onaylandi: true }
    );
    useAlert(t('META_ADS.ISTATISTIKLER.DONUSUMLER.OLUSTURULDU'));
    formAcik.value = false;
    yeniAd.value = '';
    yeniUrl.value = '';
    await donusumleriYukle();
  } catch (e) {
    donusumHata.value = e.response?.data?.error || e.message;
  } finally {
    kaydediliyor.value = false;
  }
};

const hepsiniYukle = () => {
  pikselleriYukle();
  donusumleriYukle();
};

// Hesap degisiminde acik olan piksel ayrinti paneli onceki hesaba ait;
// listeler yenilenirken panel kapatilmazsa yeni hesabin verisi saniliyor.
watch(() => props.hesap, () => {
  seciliPiksel.value = null;
  pikselOzeti.value = '';
  pikselVerisi.value = null;
  pikselOlaylari.value = [];
  veriKalitesi.value = '';
  ayrintiHata.value = '';
  yeniPiksel.value = '';
  hepsiniYukle();
});
onMounted(hepsiniYukle);
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <p v-if="!hesap" class="text-sm text-n-slate-11">
      {{ t('META_ADS.ISTATISTIKLER.PERFORMANS.HESAP_SEC') }}
    </p>

    <template v-else>
      <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section class="flex flex-col gap-2">
          <div class="flex items-center gap-3">
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('META_ADS.ISTATISTIKLER.DONUSUMLER.PIKSELLER') }}
            </h2>
            <Button
              variant="faded"
              size="sm"
              icon="i-lucide-refresh-cw"
              :label="t('META_ADS.ISTATISTIKLER.DONUSUMLER.YENILE')"
              @click="pikselleriYukle"
            />
          </div>

          <p v-if="pikselHata" class="text-sm text-n-ruby-11">{{ pikselHata }}</p>

          <AracTablosu
            :satirlar="pikseller"
            :sutunlar="pikselSutunlari"
            :yukleniyor="pikselYukleniyor"
            :bos-metin="t('META_ADS.ISTATISTIKLER.DONUSUMLER.PIKSEL_YOK')"
          >
            <template #eylemler="{ satir }">
              <Button
                variant="faded"
                size="sm"
                icon="i-lucide-info"
                :label="t('META_ADS.ISTATISTIKLER.DONUSUMLER.AYRINTI')"
                @click="pikselSec(satir)"
              />
            </template>
          </AracTablosu>
        </section>

        <section class="flex flex-col gap-2">
          <div class="flex items-center gap-3">
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('META_ADS.ISTATISTIKLER.DONUSUMLER.OZEL_DONUSUMLER') }}
            </h2>
            <Button
              variant="faded"
              size="sm"
              icon="i-lucide-plus"
              :label="t('META_ADS.ISTATISTIKLER.DONUSUMLER.YENI')"
              @click="formAcik = !formAcik"
            />
          </div>

          <p v-if="donusumHata" class="text-sm text-n-ruby-11">
            {{ donusumHata }}
          </p>

          <div
            v-if="formAcik"
            class="flex flex-col gap-3 p-4 border rounded-lg border-n-weak"
          >
            <input
              v-model="yeniAd"
              type="text"
              class="!mb-0"
              :placeholder="t('META_ADS.ISTATISTIKLER.DONUSUMLER.ALAN.AD')"
            />
            <input
              v-model="yeniPiksel"
              type="text"
              class="!mb-0"
              :placeholder="t('META_ADS.ISTATISTIKLER.DONUSUMLER.ALAN.PIKSEL')"
            />
            <select v-model="yeniOlay" class="!mb-0">
              <option v-for="o in olayTurleri" :key="o" :value="o">{{ o }}</option>
            </select>
            <input
              v-model="yeniUrl"
              type="text"
              class="!mb-0"
              :placeholder="t('META_ADS.ISTATISTIKLER.DONUSUMLER.ALAN.URL')"
            />
            <div class="flex gap-2">
              <Button
                size="sm"
                icon="i-lucide-check"
                :label="t('META_ADS.ISTATISTIKLER.DONUSUMLER.KAYDET')"
                :disabled="!yeniAd || !yeniPiksel || kaydediliyor"
                @click="donusumOlustur"
              />
              <Button
                variant="faded"
                size="sm"
                :label="t('META_ADS.ISTATISTIKLER.DONUSUMLER.VAZGEC')"
                @click="formAcik = false"
              />
            </div>
          </div>

          <AracTablosu
            :satirlar="donusumler"
            :sutunlar="donusumSutunlari"
            :yukleniyor="donusumYukleniyor"
            :bos-metin="t('META_ADS.ISTATISTIKLER.DONUSUMLER.DONUSUM_YOK')"
          />
        </section>
      </div>

      <section
        v-if="seciliPiksel"
        class="flex flex-col gap-3 pt-4 border-t border-n-weak"
      >
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.ISTATISTIKLER.DONUSUMLER.PIKSEL_AYRINTI') }}
          <span class="font-normal text-n-slate-11">
            · {{ seciliPiksel.name || seciliPiksel.id }}
          </span>
        </h2>

        <p v-if="ayrintiHata" class="text-sm text-n-ruby-11">{{ ayrintiHata }}</p>

        <div v-if="ayrintiYukleniyor" class="flex p-4">
          <Spinner class="text-n-brand" />
        </div>

        <template v-else>
          <p v-if="pikselOzeti" class="text-sm whitespace-pre-line text-n-slate-11">
            {{ pikselOzeti }}
          </p>

          <!--
            Ham JSON blogu yerine alan/deger listesi: kullanici teknik anahtar
            ve suslu parantez degil, alanin adini ve degerini goruyor.
          -->
          <dl
            v-if="pikselAlanlari.length"
            class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 p-3 text-sm border rounded-lg border-n-weak"
          >
            <template v-for="alan in pikselAlanlari" :key="alan.anahtar">
              <dt class="text-n-slate-11">{{ alan.etiket }}</dt>
              <dd class="break-all text-n-slate-12">{{ alan.deger }}</dd>
            </template>
          </dl>

          <p
            v-if="!pikselOzeti && !pikselAlanlari.length && !ayrintiHata"
            class="text-sm text-n-slate-11"
          >
            {{ t('META_ADS.ISTATISTIKLER.DONUSUMLER.AYRINTI_YOK') }}
          </p>

          <div class="flex flex-col gap-2">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t('META_ADS.ISTATISTIKLER.DONUSUMLER.OLAYLAR') }}
            </h3>
            <AracTablosu
              :satirlar="pikselOlaylari"
              :sutunlar="olaySutunlari"
              :bos-metin="t('META_ADS.ISTATISTIKLER.DONUSUMLER.OLAY_YOK')"
            />
          </div>

          <div v-if="veriKalitesi" class="flex flex-col gap-1">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t('META_ADS.ISTATISTIKLER.DONUSUMLER.VERI_KALITESI') }}
            </h3>
            <p class="text-sm whitespace-pre-line text-n-slate-11">
              {{ veriKalitesi }}
            </p>
          </div>
        </template>
      </section>
    </template>
  </div>
</template>
